import type { ReactNode } from 'react';
import * as React from 'react';
import Slider from 'react-slick';

import Imgix from 'js/components/Imgix';

import { Typography2, VisuallyHidden } from '@coursera/cds-core';
import { Col, Row } from '@coursera/coursera-ui';
import { SvgChevronLeft, SvgChevronRight } from '@coursera/coursera-ui/svg';

import type { TestimonialItemType } from 'bundles/career-plans/components/data/types/career-plans';
import Markdown from 'bundles/cms/components/Markdown';
import TrackedButton from 'bundles/page/components/TrackedButton';
import { TrackedA } from 'bundles/page/components/TrackedLink2';

import _t from 'i18n!nls/career-plans';

import 'css!./__styles__/CareerPlansTestimonials';

type Props = {
  testimonials: Array<TestimonialItemType>;
  children?: ReactNode;
};

const LeftArrowButton = ({ onClick, ...rest }: { onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void }) => (
  <div className="testimonialsSlider-left">
    <TrackedButton
      {...rest}
      style={{ padding: 0 }}
      trackingName="careerPlans_testimonial_carousel_left_button"
      aria-label={_t('Move Carousel Left')}
      onClick={onClick}
    >
      <SvgChevronLeft size={48} color="var(--cds-color-grey-700)" hoverColor="var(--cds-color-neutral-primary)" />
    </TrackedButton>
  </div>
);

const RightArrowButton = ({ onClick, ...rest }: { onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void }) => (
  <div className="testimonialsSlider-right">
    <TrackedButton
      {...rest}
      style={{ padding: 0 }}
      trackingName="careerPlans_testimonial_carousel_right_button"
      aria-label={_t('Move Carousel Right')}
      onClick={onClick}
    >
      <SvgChevronRight size={48} color="var(--cds-color-grey-700)" hoverColor="var(--cds-color-neutral-primary)" />
    </TrackedButton>
  </div>
);

const CareerPlansTestimonials = ({ testimonials }: Props) => {
  const [announcement, setAnnouncement] = React.useState(_t('You are currently on slide 1'));
  if (!testimonials?.length) return null;

  const sliderOptions = {
    infinite: true,
    nextArrow: <RightArrowButton />,
    prevArrow: <LeftArrowButton />,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    dots: true,
    initialSlide: 0,
    afterChange: (currentSlide: number) =>
      setAnnouncement(_t('You are currently on slide #{slide}', { slide: currentSlide + 1 })),
  };

  const renderSlide = ({
    name,
    job,
    title = 'Stories from the Coursera Community',
    text,
    readMoreUrl,
    imageSrc,
    remoteImg = false,
  }: $TSFixMe) => {
    if (!name || !job || !text || !imageSrc) return null;

    return (
      <div
        key={`testimonialSlide-${name}--${job}`}
        className="careerPlansTestimonials-slide"
        data-e2e="CareerPlansTestimonials-testimonial"
      >
        <div className="careerPlansTestimonials-slide--container">
          <div className="careerPlansTestimonials-slide--imageSection">
            <Typography2
              component="h2"
              className="careerPlansTestimonials-slide--title careerPlans-header careerPlans-header-mobile"
            >
              {title}
            </Typography2>
            <div className="careerPlansTestimonials-slide--image">
              {!remoteImg && <Imgix src={imageSrc} alt={`${name}, ${job}`} maxWidth={166} />}
              {remoteImg && (
                <span
                  className="careerPlansTestimonials-slide--image-span"
                  role="img"
                  aria-label={`${name}, ${job}`}
                  style={{
                    backgroundImage: `url(${imageSrc})`,
                  }}
                />
              )}
            </div>
            <div className="careerPlansTestimonials-slide--name">{name}</div>
            <div className="careerPlansTestimonials-slide--jobTitle careerPlans-sectionHeader">{job}</div>
            {readMoreUrl?.length > 0 && (
              <TrackedA
                className="careerPlansTestimonials-slide--readMore careerPlans-link is-uppercase"
                href={readMoreUrl}
                trackingName="careerPlans_testimonial_readmore"
                data-e2e="CareerPlansTestimonials-testimonial--readmore"
              >
                {_t('Read More')}
              </TrackedA>
            )}
          </div>
          <div className="careerPlansTestimonials-slide--textSection">
            <Typography2
              component="h2"
              className="careerPlansTestimonials-slide--title careerPlans-header careerPlans-header-desktop"
            >
              {title}
            </Typography2>
            <div className="careerPlansTestimonials-slide-text careerPlans-text">
              <Markdown source={text} />
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Row>
      <Col className="careerPlans-testimonials careerPlans-testimonial-bg careerPlans-grey-bg">
        <div className="careerPlans-container">
          <section className="rc-CareerPlansTestimonials" data-e2e="CareerPlansTestimonials">
            <Slider className="careerPlansTestimonials-slider" {...sliderOptions}>
              {testimonials.map(renderSlide)}
            </Slider>
            <VisuallyHidden
              aria-live="polite"
              aria-atomic={true}
              aria-relevant="text"
              component="p"
              className="rc-A11yScreenReaderOnly"
            >
              {announcement}
            </VisuallyHidden>
          </section>
        </div>
      </Col>
    </Row>
  );
};

export default CareerPlansTestimonials;
